import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { SetFiltering } from 'state/actions';
import { UserAccess, UserHasAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { FormLocationSelector, FormTextInput } from "components/form-fields";
import { Tooltip } from "components/tooltip";
import { LinkButton } from "components/buttons";
import { Filter, FilterButton } from "components/filtering";
import { NotificationToaster } from "components/notifications";
import { Intent, Position } from "@blueprintjs/core";
import { ListingPagination, ListingTable } from "components/listing";
import { AssetService, LinkService, LocationService } from 'services';
import { AssetStatus } from 'components/status/AssetStatus';
import { useDebounce } from "hooks/useDebounce";
import { InternalLink } from 'components/navigation';
import { AssetTypeFilterRenderer } from "pages/vehicle/asset-type-filter-renderer/AssetTypeFilterRenderer";
import { ShowHide } from "components/layout";

export function ManageAssets({ breadcrumbs }) {
    const requiredActions = ["Fleet:View", "FleetPCN:View", "FleetVIS:View", "FMS:View"];
    const requiredAssetCreateActions = ["Fleet:Add", "FleetPCN:Add", "FleetVIS:Add", "FMS:Edit"];
    const canAccessPage = UserHasAccess(requiredActions);
    const canCreateAsset = UserHasAccess(requiredAssetCreateActions);

    const firstColumnLabel = "ID/Registration";
    const defaultItemsPerPage = 25;
    const debounceTimeout = 750;
    const filterName = "manageAssets";
    const filterSearchSelector = "manageAssetsSearch";

    const [isInitialising, setIsInitialising] = useState(true);
    const [isSearching, setIsSearching] = useState(true);
    const [locationsLoading, setLocationsLoading] = useState(true);
    const [useLocationDefaults, setUseLocationDefaults] = useState(null);
    const [locationList, setLocationList] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [pageFilters, setPageFilters] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [useHierarchy, setUseHierarchy] = useState(true);
    const inputSearchRef = useRef(null);
    const [expandedAssetTypes, setExpandedAssetTypes] = useState({});

    const [assetStatusesMappings, setAssetStatusesMappings] = useState([]);
    const [ownershipAttributeMappings, setOwnershipAttributeMappings] = useState([]);

    const [manageAssetsSearchLoaded, setManageAssetsSearchLoaded] = useState(false);
    const manageAssetsSearch = useSelector(state => state.filters[filterSearchSelector]);

    const currentSearchTerm = useRef();
    const currentPageNo = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentLocations = useRef();

    const [searchResult, setSearchResult] = useState([]);
    const [searchResultData, setSearchResultData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [sortedBy, setSortedBy] = useState(firstColumnLabel);
    const [sortedDir, setSortedDir] = useState("A");
    const [currentPageNumber, setCurrentPageNumber] = useState(1);
    const perPage = defaultItemsPerPage;

    const [locationSelectorOpen, setLocationSelectorOpen] = useState(false);
    const dispatch = useDispatch();

    const tableHeaders = [firstColumnLabel, "Asset Type", "Location", "Status", "Ownership", "Fleet Number"];
    const sortableHeaders = [firstColumnLabel, "Asset Type", "Location", "Status", "Ownership", "Fleet Number"];

    function formatPageFilters(statusesForAssets, assetOwnerships, assetTypes) {
        return [
            {
                name: "Status",
                displayName: "Status",
                items: statusesForAssets
            },
            {
                name: "Ownership",
                displayName: "Ownership",
                items: assetOwnerships
            },
            {
                name: "AssetType",
                displayName: "Type",
                items: assetTypes
            }
        ];
    }

    function processAssetOptions(response) {
        if (response != null) {
            let assetTypes = response.assetTypes;
            setPageFilters(formatPageFilters(response.assetStatuses, response.assetOwnerships, assetTypes));
            setOwnershipAttributeMappings(response.assetOwnerships.map((x) => {
                return { name: x.name, colours: x.colour, id: x.id, className: x.style, icon: x.icon };
            }));
            setAssetStatusesMappings(response.assetStatuses.map((x) => {
                return { name: x.name, colours: x.colour, id: x.id };
            }));
        }
    }

    useEffect(() => {
        if (!canAccessPage) {
            return;
        }

        const assetOptionsPromise = AssetService.getAssetOptions(true).then((assetOptionsResponse) => {
            processAssetOptions(assetOptionsResponse);
        }).catch(() => {
            NotificationToaster.show(Intent.DANGER, "Failed to load Manage Assets. Please refresh the page.");
        });

        const locationsPromise = LocationService.getFormLocationSelectorCustomLocations(['Tacho', 'Vis']).then((locations) => {
            setLocationList(locations);
        }).catch(() => {
            NotificationToaster.show(Intent.DANGER, "Failed to load locations. Please refresh the page.");
        });

        Promise.all([assetOptionsPromise, locationsPromise]).finally(() => {
            setIsInitialising(false);
        });
    }, []);

    const defaultFilters = [
        { name: 'Status', displayName: 'Active', value: 1 },
        { name: 'Status', displayName: 'Vehicle Off Road', value: 3 }
    ];
    let selectedFilters = useSelector(state => state.filters[filterName]) || [];

    useEffect(() => {
        if (selectedFilters.length === 0) {
            dispatch(SetFiltering(filterName, defaultFilters));
        }
    }, [selectedFilters]);

    function onSelectedLocationsChange(locations) {
        setSelectedLocations(locations);
        setManageAssetsSearchDispatch(locations, null, null, null, null);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => !prevState);
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
    }

    function onLocationListOpened() {
        setLocationSelectorOpen(true);
    }

    function onLocationListClose() {
        setLocationSelectorOpen(false);
    }

    function onSearchTextChange(item) {
        setSearchTerm(item);
        setManageAssetsSearchDispatch(null, item, null, null, null);
    }

    function onPagingChange(page) {
        setCurrentPageNumber(page);
        setManageAssetsSearchDispatch(null, null, null, page, null);
    }

    function onTableSort(header, direction) {
        setSortedBy(header);
        setSortedDir(direction);
        setManageAssetsSearchDispatch(null, null, header, null, direction);
    }

    function setManageAssetsSearchDispatch(newLocations, newSearchTerm, newSortedBy, newCurrentPageNumber, newSortedDir) {
        dispatch(SetFiltering(filterSearchSelector, {
            locations: newLocations ?? selectedLocations,
            searchTerm: newSearchTerm ?? searchTerm,
            sortedBy: newSortedBy ?? sortedBy,
            currentPageNumber: newCurrentPageNumber ?? currentPageNumber,
            sortedDir: newSortedDir ?? sortedDir
        }));
    }

    useEffect(() => {
        if (selectedLocations.length > 0) {
            setLocationsLoading(false);
        }
    }, [selectedLocations]);

    useEffect(function () {
        if (manageAssetsSearch && !manageAssetsSearchLoaded) {
            setSelectedLocations(manageAssetsSearch.locations);
            setSearchTerm(manageAssetsSearch.searchTerm);
            setSortedBy(manageAssetsSearch.sortedBy);
            setCurrentPageNumber(manageAssetsSearch.currentPageNumber);
            setSortedDir(manageAssetsSearch.sortedDir);
            setUseLocationDefaults(false);
        } else {
            setUseLocationDefaults(true);
        }
        if (!manageAssetsSearchLoaded) {
            setManageAssetsSearchLoaded(true);
        }
    }, [manageAssetsSearch]);

    const sortFieldMap = {
        [firstColumnLabel]: "AssetRegistration",
        "Asset Type": "AssetType.Name",
        "Location": "Location.Name",
        "Status": "AssetStatus.Name",
        "Ownership": "AssetOwnership.Name",
        "Fleet Number": "FleetNumber"
    };

    function getSearchPayload() {
        let filters = appliedFilters.map(x => {
            if (x.name === "AssetTypeSubtype") {
                const assetTypeItems = pageFilters.find(f => f.name === "AssetType")?.items || [];
                const parentType = assetTypeItems.find(t => t.id === x.parentAssetTypeId);
                return { key: "AssetType", value: parentType?.name || x.displayName };
            }
            if (x.name === "AssetType") {
                return { key: x.name, value: x.displayName };
            }
            return { key: x.name, value: x.displayName };
        });

        if (selectedLocations != null) {
            selectedLocations.forEach(function (l) {
                filters = [...filters, { key: "Location", value: l }];
            });
        }

        // Deduplicate AssetType filters (multiple subtypes of same parent)
        const assetTypeFilters = filters.filter(f => f.key === "AssetType");
        const otherFilters = filters.filter(f => f.key !== "AssetType");
        const uniqueAssetTypes = [...new Set(assetTypeFilters.map(f => f.value))];
        filters = [...otherFilters, ...uniqueAssetTypes.map(v => ({ key: "AssetType", value: v }))];

        return {
            requestCount: perPage,
            pageNumber: currentPageNumber,
            searchTerm: searchTerm,
            sortField: sortFieldMap[sortedBy] || "AssetId",
            sortDirection: sortedDir,
            filters: filters
        };
    }

    function performSearch(forceSearch) {
        const searchPayload = getSearchPayload();

        if (appliedFilters.length === 0) {
            return;
        }

        if (!forceSearch && (!searchPayload ||
            (currentPageNo.current === searchPayload.pageNumber &&
                currentSearchTerm.current === searchPayload.searchTerm &&
                currentTableSortBy.current === searchPayload.sortField &&
                currentTableSortDir.current === searchPayload.sortDirection &&
                currentAppliedFilters.current === appliedFilters.length &&
                currentLocations.current === selectedLocations.length))
        ) {
            return;
        }

        setIsSearching(true);

        if (currentPageNo.current === searchPayload.pageNumber) {
            searchPayload.pageNumber = 1;
            currentPageNo.current = 1;
            setCurrentPageNumber(1);
        } else {
            currentPageNo.current = searchPayload.pageNumber;
        }

        currentSearchTerm.current = searchPayload.searchTerm;
        currentTableSortBy.current = searchPayload.sortField;
        currentTableSortDir.current = searchPayload.sortDirection;
        currentAppliedFilters.current = appliedFilters.length;
        currentLocations.current = selectedLocations.length;

        AssetService.searchAssets(searchPayload).then((response) => {
            if (response.totalCount === 0) {
                setSearchResultData([]);
                setSearchResult([]);
                setTotalRecords(0);
                return;
            }

            if (response.data != null) {
                setSearchResultData(response.data);
            }
            setTotalRecords(response.totalCount);
        }).catch(() => {
            NotificationToaster.show(Intent.DANGER, "Asset search failed. Please try again.");
        }).finally(function () {
            setIsSearching(false);
        });
    }

    useEffect(() => {
        if (isInitialising || locationsLoading || !manageAssetsSearchLoaded || locationSelectorOpen) { return; }
        performSearch();
    }, [debouncedSearchTerm, appliedFilters, selectedLocations, sortedBy, sortedDir, perPage, currentPageNumber, locationsLoading, locationSelectorOpen, isInitialising]);



    useEffect(() => {
        if (searchResultData !== undefined && assetStatusesMappings.length > 0 && ownershipAttributeMappings.length > 0) {
            const data = searchResultData.map((x) => {
                return [
                    <div className="nowrap inline-items no-spacing" key={`asset_${x.assetId}`}>
                        <AssetStatus
                            statusId={x.assetStatus.id}
                            ownershipId={x.assetOwnership.id}
                            isLoading={isInitialising}
                            statusAttributeMappings={assetStatusesMappings}
                            ownershipAttributeMappings={ownershipAttributeMappings}
                            tooltipPosition={Position.RIGHT}
                        />
                        <Tooltip position={Position.TOP} content="Asset Profile">
                            <InternalLink link={LinkService.getVehicleProfileUrl(x.assetId, 'details', 'area=MMV')}>{x.assetRegistration}</InternalLink>
                        </Tooltip>
                    </div>,
                    x.assetType?.name || "",
                    x.location?.name || "",
                    x.assetStatus?.name || "",
                    x.assetOwnership?.name || "",
                    x.fleetNumber || ""
                ];
            });
            setSearchResult(data);
        }
    }, [searchResultData, assetStatusesMappings, ownershipAttributeMappings]);

    useEffect(() => {
        setTotalPages(Math.ceil(totalRecords / perPage));
    }, [totalRecords, perPage]);

    return (
        <UserAccess perform={requiredActions}
            yes={() => (
                <>
                    <h1>Manage Assets</h1>

                    <ShowHide
                        evaluator={canCreateAsset}
                        show={(
                            <div className="button-row button-row-stacked-mobile spacer-bottom">
                                <LinkButton
                                    intent="primary"
                                    icon="plus"
                                    href="/asset/create/"
                                    id="manage-assets-create"
                                    text="Add New Asset"
                                />
                            </div>
                        )}
                    />

                    <div className="spacer-bottom">
                        <div className="filter-controls">
                            <div className="inline-items">
                                <div className="inline-item fill">
                                    <div className="form-field-full">
                                        <FormTextInput
                                            inputRef={inputSearchRef}
                                            placeholder="Search by ID/Registration"
                                            onChange={(x) => onSearchTextChange(x.target.value)}
                                            value={searchTerm}
                                            large
                                            disabled={isInitialising}
                                            icon="search"
                                            id="manage-assets-search-field"
                                        />
                                    </div>
                                </div>
                                <div className="inline-item">
                                    <FormLocationSelector
                                        businessArea={""}
                                        loading={isInitialising}
                                        onLocationListOpened={onLocationListOpened}
                                        onLocationListClose={onLocationListClose}
                                        selectedLocations={selectedLocations}
                                        setSelectedLocations={onSelectedLocationsChange}
                                        useLocationDefaults={useLocationDefaults}
                                        useHierarchy={useHierarchy}
                                        setUseHierarchy={setUseHierarchy}
                                        locationDefaultsToTrue={useLocationDefaults}
                                        customLocations={locationList}
                                        useCustomLocations={true}
                                    />
                                </div>
                                <div className="inline-item">
                                    <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                                </div>
                            </div>
                            <Filter
                                filterName={filterName}
                                visible={showFilters}
                                filters={pageFilters}
                                onUpdate={onFilterChange}
                                onClickOutside={onToggleFilter}
                                customRenderers={{
                                    AssetType: (props) => (
                                        <AssetTypeFilterRenderer
                                            {...props}
                                            expandedAssetTypes={expandedAssetTypes}
                                            setExpandedAssetTypes={setExpandedAssetTypes}
                                        />
                                    )
                                }}
                            />
                        </div>
                    </div>

                    <ListingTable
                        id="listing-table-manage-assets"
                        headers={tableHeaders}
                        data={searchResult}
                        totalRecordCount={totalRecords}
                        onPagingChange={onPagingChange}
                        loadingData={isSearching || isInitialising}
                        noDataHeading="No Assets Found"
                        noDataMessage="No assets matched your search criteria."
                        sortable
                        sortableHeaders={sortableHeaders}
                        sortedBy={sortedBy}
                        sortedDir={sortedDir}
                        onSort={onTableSort}
                        pageable={false}
                    />

                    <ShowHide
                        evaluator={totalRecords > 0}
                        show={(
                            <ListingPagination
                                currentPage={currentPageNumber}
                                totalPages={totalPages}
                                onPageChange={onPagingChange}
                                loadingData={isSearching || isInitialising}
                            />
                        )}
                    />
                </>
            )}
            no={() => <PageRestricted />}
        />
    );
}
